import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/reactApi/api';

class AccountsApiService {
  constructor() {
    this.baseURL = `${API_BASE_URL}/accounts`;
  }

  // Create axios instance with default config
  createApiInstance() {
    return axios.create({
      baseURL: this.baseURL,
      timeout: 30000,
      headers: {
        'Content-Type': 'application/json',
      },
    });
  }

  // Payment Details API calls
  async getPaymentDetails(filters = {}) {
    try {
      const api = this.createApiInstance();
      const response = await api.get('/payment-details', { params: filters });
      return response.data;
    } catch (error) {
      console.error('Error fetching payment details:', error);
      throw this.handleError(error);
    }
  }

  async exportPaymentDetails(filters = {}) {
    try {
      const api = this.createApiInstance();
      const response = await api.get('/payment-details/export', { 
        params: filters,
        responseType: 'blob'
      });
      
      // Create download link
      const blob = new Blob([response.data], { 
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'PaymentReport.xlsx';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      return { success: true, message: 'Export completed' };
    } catch (error) {
      console.error('Error exporting payment details:', error);
      throw this.handleError(error);
    }
  }

  // Dropdown data API calls
  async getPaymentTypes() {
    try {
      const api = this.createApiInstance();
      const response = await api.get('/payment-types');
      return response.data;
    } catch (error) {
      console.error('Error fetching payment types:', error);
      throw this.handleError(error);
    }
  }

  async getAssessmentTypes() {
    try {
      const api = this.createApiInstance();
      const response = await api.get('/assessment-types');
      return response.data;
    } catch (error) {
      console.error('Error fetching assessment types:', error);
      throw this.handleError(error);
    }
  }

  // Dashboard API calls
  async getDashboardStats() {
    try {
      const api = this.createApiInstance();
      const response = await api.get('/dashboard/stats');
      return response.data;
    } catch (error) {
      console.error('Error fetching dashboard stats:', error);
      throw this.handleError(error);
    }
  }

  // Transaction Report API calls
  async getTransactionReport(filters = {}) {
    try {
      const api = this.createApiInstance();
      const response = await api.get('/transaction-report', { params: filters });
      return response.data;
    } catch (error) {
      console.error('Error fetching transaction report:', error);
      throw this.handleError(error);
    }
  }

  // Invoice Management API calls
  async getInvoices(filters = {}) {
    try {
      const api = this.createApiInstance();
      const response = await api.get('/invoices', { params: filters });
      return response.data;
    } catch (error) {
      console.error('Error fetching invoices:', error);
      throw this.handleError(error);
    }
  }

  // Get invoice details for viewing
  async getInvoiceDetails(appId, payType, payid) {
    try {
      const api = this.createApiInstance();
      const response = await api.get('/invoice', { 
        params: { appId, payType, payid } 
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching invoice details:', error);
      throw this.handleError(error);
    }
  }

  // Generate invoice PDF
  async generateInvoicePDF(appId, payType, payid) {
    try {
      const api = this.createApiInstance();
      const response = await api.get('/invoice/pdf', { 
        params: { appId, payType, payid }
      });
      
      // Check if backend indicates client-side generation
      if (response.data.generateClientSide) {
        return response.data;
      }
      
      // If backend provides actual blob data, handle as before
      if (response.data instanceof Blob) {
        const blob = new Blob([response.data], { type: 'application/pdf' });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `Invoice_${appId}_${payType}_${payid}.pdf`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
        
        return { success: true, message: 'PDF downloaded successfully' };
      }
      
      return response.data;
    } catch (error) {
      console.error('Error generating invoice PDF:', error);
      throw this.handleError(error);
    }
  }

  // Get unpaid assessment proforma invoice details
  async getUnpaidAssessmentProformaInvoice(appId, payType, id) {
    try {
      const api = this.createApiInstance();
      const response = await api.get('/unpaid-assessment-proforma-invoice', {
        params: { appId, payType, id }
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching assessment proforma invoice:', error);
      throw this.handleError(error);
    }
  }

  // Get assessment type for invoice redirects
  async getAssessmentType(appId) {
    try {
      const api = this.createApiInstance();
      const response = await api.get('/assessment-type', { 
        params: { appId } 
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching assessment type:', error);
      throw this.handleError(error);
    }
  }

  // Health check
  async healthCheck() {
    try {
      const api = this.createApiInstance();
      const response = await api.get('/health');
      return response.data;
    } catch (error) {
      console.error('Error in health check:', error);
      throw this.handleError(error);
    }
  }

  // Initiate outstanding payment via ICICI EazyPay
  async initiateOutstandingPayment({ orgId, appId, totalAmount, invoiceNumbers, labTypeId }) {
    try {
      const api = this.createApiInstance();
      const response = await api.post('/outstanding/initiate-payment', {
        orgId,
        appId,
        totalAmount,
        invoiceNumbers,
        labTypeId
      });
      return response.data;
    } catch (error) {
      console.error('Error initiating outstanding payment:', error);
      throw this.handleError(error);
    }
  }

  // Get outstanding amount from Business Central
  async getOutstandingAmount(customerNo) {
    try {
      const api = this.createApiInstance();
      const response = await api.get('/business-central/customers', {
        params: { customerNo }
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching outstanding amount:', error);
      throw this.handleError(error);
    }
  }

  // Get ledger entries from Business Central (NABLcustomerLedgerEntries)
  async getLedgerEntries(customerNo) {
    try {
      const api = this.createApiInstance();
      const response = await api.get('/business-central/ledger-entries', {
        params: { customerNo }
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching ledger entries:', error);
      throw this.handleError(error);
    }
  }

  // Error handling helper
  handleError(error) {
    if (error.response) {
      // Server responded with error status
      const errorMessage = error.response.data?.message || 
                          error.response.data?.error || 
                          `HTTP Error ${error.response.status}`;
      return {
        success: false,
        message: errorMessage,
        status: error.response.status,
        data: error.response.data
      };
    } else if (error.request) {
      // Request was made but no response received
      return {
        success: false,
        message: 'Network error - please check your connection',
        status: 0,
        data: null
      };
    } else {
      // Something else happened
      return {
        success: false,
        message: error.message || 'An unexpected error occurred',
        status: 0,
        data: null
      };
    }
  }

  // Utility method to format API responses
  formatResponse(response) {
    if (response && response.data) {
      return response.data;
    }
    return response;
  }

  // Lab types (can be moved to a common service if needed elsewhere)
  getLabTypes() {
    return [
      { id: '1', name: 'Testing Laboratory' },
      { id: '2', name: 'Calibration Laboratory' },
      { id: '3', name: 'Medical Laboratory' }
    ];
  }

  // Payment statuses for filtering
  getPaymentStatuses() {
    return [
      { value: '', label: 'All Transactions' },
      { value: '1', label: 'Successful Transactions' },
      { value: '2', label: 'Unsuccessful Transactions' }
    ];
  }

  // Invoice statuses
  getInvoiceStatuses() {
    return [
      { value: '', label: 'All Statuses' },
      { value: 'Paid', label: 'Paid' },
      { value: 'Pending', label: 'Pending' },
      { value: 'Overdue', label: 'Overdue' },
      { value: 'Cancelled', label: 'Cancelled' }
    ];
  }

  // Check assessment fees payment eligibility
  async checkAssessmentFeesEligibility(appId) {
    try {
      const api = this.createApiInstance();
      const response = await api.get('/assessment-fees-eligibility', { 
        params: { appId } 
      });
      return response.data;
    } catch (error) {
      console.error('Error checking assessment fees eligibility:', error);
      throw this.handleError(error);
    }
  }

  // Get approval details for accounts view
  async getApprovalDetails(params = {}) {
    try {
      const api = this.createApiInstance();
      const response = await api.get('/approval-details', { params });
      return response.data;
    } catch (error) {
      console.error('Error fetching approval details:', error);
      throw this.handleError(error);
    }
  }

  // Submit approval details
  async submitApprovalDetails(data) {
    try {
      const api = this.createApiInstance();
      const response = await api.post('/approval-details', data);
      return response.data;
    } catch (error) {
      console.error('Error submitting approval details:', error);
      throw this.handleError(error);
    }
  }

  // Send to CEO
  async sendToCEO(data) {
    try {
      const api = this.createApiInstance();
      const response = await api.post('/approval-details/send-to-ceo', data);
      return response.data;
    } catch (error) {
      console.error('Error sending to CEO:', error);
      throw this.handleError(error);
    }
  }

  // Send to lab for payment
  async sendToLabPayment(data) {
    try {
      const api = this.createApiInstance();
      const response = await api.post('/approval-details/send-to-lab', data);
      return response.data;
    } catch (error) {
      console.error('Error sending to lab for payment:', error);
      throw this.handleError(error);
    }
  }
}

// Create and export singleton instance
const accountsApiService = new AccountsApiService();
export default accountsApiService;

// Named exports for specific functions if needed
export const {
  getPaymentDetails,
  exportPaymentDetails,
  getPaymentTypes,
  getAssessmentTypes,
  getDashboardStats,
  getTransactionReport,
  getInvoices,
  healthCheck,
  checkAssessmentFeesEligibility,
  getApprovalDetails,
  submitApprovalDetails,
  sendToCEO,
  sendToLabPayment
} = accountsApiService;